import { useState, useEffect, useCallback } from 'react';
import { adminServices } from 'services';

const usePromotions = (project, projectId, updateDraft) => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [selectedPromotionIds, setSelectedPromotionIds] = useState([]);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);

  // Fetch categories and promotions
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const [categoriesData, promotionsData] = await Promise.all([
        adminServices.getPromotionCategories(),
        adminServices.getPromotionItems(),
      ]);

      setCategories(categoriesData || []);
      setPromotions(promotionsData || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching promotions:', error);
      setLoading(false);
    }
  }, []);

  // Load previously selected promotions from project
  useEffect(() => {
    fetchData();

    if (project?.selected_promotion_ids && Array.isArray(project.selected_promotion_ids)) {
      setSelectedPromotionIds(project.selected_promotion_ids);
    }
  }, [fetchData, project?.selected_promotion_ids]);

  // Get promotions by category
  const getPromotionsByCategory = useCallback(
    (categoryId) => {
      return promotions.filter((p) => p.category?.id === categoryId);
    },
    [promotions]
  );

  // Modal handlers
  const openInfoModal = useCallback(() => setShowInfoModal(true), []);
  const closeInfoModal = useCallback(() => setShowInfoModal(false), []);

  const openDetailModal = useCallback((promotion) => {
    setSelectedPromotion(promotion);
    setShowDetailModal(true);
  }, []);

  const closeDetailModal = useCallback(() => {
    setShowDetailModal(false);
    setSelectedPromotion(null);
  }, []);

  // Toggle promotion selection
  const handlePromotionSelect = useCallback((promotion) => {
    openDetailModal(promotion);
  }, [openDetailModal]);

  // Confirm selection from modal
  const confirmPromotionSelection = useCallback(() => {
    if (selectedPromotion && !selectedPromotionIds.includes(selectedPromotion.id)) {
      const newSelectedIds = [...selectedPromotionIds, selectedPromotion.id];
      setSelectedPromotionIds(newSelectedIds);

      // Save to draft immediately
      if (projectId) {
        updateDraft(projectId, { selected_promotion_ids: newSelectedIds });
      }
    }
    closeDetailModal();
  }, [selectedPromotion, selectedPromotionIds, projectId, updateDraft, closeDetailModal]);

  // Save and continue
  const savePromotions = useCallback(() => {
    if (projectId) {
      updateDraft(projectId, {
        selected_promotion_ids: selectedPromotionIds,
        is_promotions_done: true,
      });
    }
  }, [projectId, selectedPromotionIds, updateDraft]);

  return {
    loading,
    categories,
    selectedPromotionIds,
    showInfoModal,
    showDetailModal,
    selectedPromotion,
    getPromotionsByCategory,
    openInfoModal,
    closeInfoModal,
    closeDetailModal,
    handlePromotionSelect,
    confirmPromotionSelection,
    savePromotions,
  };
};

export default usePromotions;
